/* eslint-disable react/no-danger */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable max-len */
/* eslint-disable no-nested-ternary */
import React, {
  useState, useRef, useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';
import { debounce } from 'lodash';
import ClipLoader from 'react-spinners/ClipLoader';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import useValidateUrl from './hooks/useValidateUrl';

import {
  updateProgrammedCalendarItem,
  updateSavedWorkoutItem,
  setCombineWithInfo,
  swapProgrammedItemOnChildCalendar,
  setActiveModal,
} from '../../ducks/calendarActions';
import { getDebouncedHandlerForTable, flushDebounceForTable } from './hooks/debounceHandler';

const Container = styled('div')`
  display: flex;
  width: 100%;
  justify-content: space-between;
  gap: 16px;
`;

const VideoUrlInput = styled('input')`
  display: flex;
  width: 100%; 
  border: 1px solid #EEEEEE;
  border-radius: 4px;
  min-height: 34px;
  max-height: 34px;
  min-width: 200px;
  background: #FCFCFC;
  padding-left: 15px;
  padding-right: 30px;
  color: #424242;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;


   &::placeholder {
    font-family: 'Nunito Sans', sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #9E9E9E;
  }
`;

const InputContainer = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const InputTitle = styled('div')`
  font-size: 16px;
  color: #424242;
  font-family: 'Nunito Sans';
  text-decoration: ${(props) => props.clickableLink && 'underline'};
  color: ${(props) => props.clickableLink && 'blue'};
  cursor: ${(props) => props.clickableLink && 'pointer'};
  width: fit-content;
`;

const Wrapper = styled('div')`
  display: flex;
  width: 100%;
  position: relative;
`;

const LoaderWrapper = styled('div')`
  position: absolute;
  top: 7px;
  right: 5px;
`;

const VideoTable = ({
  contentIndex,
  content,
  nonSwappedItemFromParentCalendar,
  date,
  dayNumber,
}) => {
  const dispatch = useDispatch();
  const {
    handleValidateUrl,
  } = useValidateUrl();

  const [videoUrl, setVideoUrl] = useState(content.url);
  const [videoTitle, setVideoTitle] = useState(content.title);
  const [firstLoad, setFirstLoad] = useState(true);
  const [passedUrlValidation, setPassedUrlValidation] = useState(!!content.url);
  const [urlValidationError, setUrlValidationError] = useState(null);
  const [urlValidationLoading, setUrlValidationLoading] = useState(false);

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const effectiveDate = date || selectedDate;
  const programType = useSelector((state) => state.calendar.data.programType);
  const combineWithInfo = useSelector((state) => state.calendar.data.combineWithInfo);

  const debouncedHandleValidateVideo = useRef(
    debounce((
      dbUrl,
    ) => {
      if (dbUrl) {
        setUrlValidationLoading(true);
        handleValidateUrl(dbUrl, setUrlValidationLoading, setPassedUrlValidation, setUrlValidationError);
      }
    }, 500),
  ).current;

  const debouncedUpdate = getDebouncedHandlerForTable(content.assignedId, (saveParams) => {
    const {
      assignedId,
      // eslint-disable-next-line no-shadow
      date,
      dayNumberParam,
      order,
      description,
      url,
      title,
      accountCode,
      dbNonSwappedItemFromParentCalendar,
      reorder,
      groupingLetter,
      groupingColorCode,
    } = saveParams;

    const updateItem = {
      assignedId,
      date,
      order,
      description,
      type: 'video',
      url,
      popUp: false,
    };
    if (programType === 'free-form-program') {
      updateItem.dayNumber = dayNumberParam;
    }

    if (title) {
      updateItem.title = title;
    }
    if (groupingLetter) {
      updateItem.groupingLetter = groupingLetter;
    }
    if (groupingColorCode) {
      updateItem.groupingColorCode = groupingColorCode;
    }

    if (programType === 'free-form-program') {
      dispatch(updateSavedWorkoutItem(
        accountCode,
        updateItem,
        order,
        dayNumberParam,
        reorder,
      ));
    }
    if (programType === 'calendar') {
      if (dbNonSwappedItemFromParentCalendar) {
        dispatch(swapProgrammedItemOnChildCalendar(
          accountCode,
          updateItem,
          order,
          date,
          reorder,
        ));
      } else {
        dispatch(updateProgrammedCalendarItem(
          accountCode,
          updateItem,
          order,
          date,
          reorder,
        ));
      }
    }
  });

  /** watch for updates to video url and validate any changes to the url
   * we are debouncing by just half a second to give people a chance to type the url
   */
  useEffect(() => {
    if (videoUrl && !firstLoad) {
      setPassedUrlValidation(false);
      setUrlValidationError(null);
      debouncedHandleValidateVideo(videoUrl, videoTitle);
    } else {
      setFirstLoad(false);
    }
  }, [videoUrl]);

  const prevPassedValidationRef = useRef(passedUrlValidation);
  useEffect(() => {
    if (firstLoad) {
      setFirstLoad(false);
      prevPassedValidationRef.current = passedUrlValidation;
      return;
    }

    if (!urlValidationLoading && passedUrlValidation && videoUrl) {
      const urlJustBecameValid = passedUrlValidation && !prevPassedValidationRef.current;

      debouncedUpdate({
        assignedId: content.assignedId,
        date: moment(effectiveDate).format('YYYY-MM-DD'),
        ...(programType === 'free-form-program' && { dayNumber }),
        order: contentIndex,
        description: content?.exercise?.description || '',
        url: videoUrl,
        title: videoTitle,
        accountCode: currentUser.accountCode,
        dbNonSwappedItemFromParentCalendar: nonSwappedItemFromParentCalendar,
        reorder: false,
        groupingLetter: content?.groupingLetter,
        groupingColorCode: content?.groupingColorCode,
      });

      if (urlJustBecameValid) {
      /** flush ONLY when URL transitions to valid */
        flushDebounceForTable(content.assignedId);
      }
    }

    prevPassedValidationRef.current = passedUrlValidation;
  }, [videoTitle, passedUrlValidation]);

  useEffect(() => {
    const isSource = combineWithInfo?.source?.length
      ? combineWithInfo.source.some((item) => item?.assignedId.toString() === content?.assignedId.toString())
      : false;

    const sourceItem = combineWithInfo?.source?.find(
      (item) => item?.assignedId.toString() === content?.assignedId.toString(),
    );

    const isTarget = combineWithInfo?.target?.assignedId === content?.assignedId;

    const targetItem = combineWithInfo?.target;

    if (!content.preSave && !urlValidationLoading && passedUrlValidation && videoUrl && (combineWithInfo && ((isSource) || (isTarget)))) {
      dispatch(setCombineWithInfo(null));
      debouncedUpdate({
        assignedId: content.assignedId,
        date: moment(effectiveDate).format('YYYY-MM-DD'),
        /** Only include dayNumber when programType === 'free-form-program' */
        ...(programType === 'free-form-program' && { dayNumber }),
        order: contentIndex,
        description: content?.exercise?.description || '',
        url: videoUrl,
        title: videoTitle,
        accountCode: currentUser.accountCode,
        dbNonSwappedItemFromParentCalendar: nonSwappedItemFromParentCalendar,
        reorder: true,
        groupingLetter: isSource
          ? sourceItem?.groupingLetter
          : isTarget
            ? targetItem?.groupingLetter
            : content?.groupingLetter,
        groupingColorCode: isSource
          ? sourceItem?.groupingColorCode
          : isTarget
            ? targetItem?.groupingColorCode
            : content?.groupingColorCode,
      });
      flushDebounceForTable(content.assignedId);
    }
  }, [combineWithInfo]);

  const handleImmediateSave = (reorder = false) => {
    if (!videoUrl || !passedUrlValidation || urlValidationLoading) return;

    debouncedUpdate({
      assignedId: content.assignedId,
      date: moment(effectiveDate).format('YYYY-MM-DD'),
      ...(programType === 'free-form-program' && { dayNumber }),
      order: contentIndex,
      description: content?.exercise?.description || '',
      url: videoUrl,
      title: videoTitle,
      accountCode: currentUser.accountCode,
      dbNonSwappedItemFromParentCalendar: nonSwappedItemFromParentCalendar,
      reorder,
      groupingLetter: content?.groupingLetter,
      groupingColorCode: content?.groupingColorCode,
    });

    /** force immediate execution */
    flushDebounceForTable(content.assignedId);
  };

  return (
    <Container>
      <InputContainer>
        <InputTitle>
          Video Url
        </InputTitle>
        <Wrapper>
          <VideoUrlInput
            value={videoUrl}
            placeholder='Paste video URL'
            onChange={(e) => {
              setVideoUrl(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleImmediateSave();
              }
            }}
          />
          {videoUrl && urlValidationError && !urlValidationLoading && (
          <IcomoonReact
            color='#CC0404'
            icon='remove'
            iconSet={iconSet}
            size={20}
            style={{
              border: '1px solid #CC0404',
              borderRadius: '50%',
              top: 7,
              right: 5,
              padding: '1px',
              position: 'absolute',
              cursor: 'pointer',
            }}
            onClick={() => setVideoUrl('')}
          />
          )}
          {videoUrl && passedUrlValidation && !urlValidationLoading && (
          <IcomoonReact
            color=' #0DCC8A'
            icon='completion-circle'
            iconSet={iconSet}
            size={20}
            style={{
              borderRadius: '50%',
              top: 7,
              right: 5,
              padding: '1px',
              position: 'absolute',
              cursor: 'pointer',
            }}
            onClick={() => {
              dispatch(setActiveModal('videoModal', { url: videoUrl }));
            }}
          />
          )}
          <LoaderWrapper>
            <ClipLoader
              color='grey'
              loading={urlValidationLoading}
              size={20}
              aria-label='Loading Spinner'
              data-testid='loader'
            />
          </LoaderWrapper>
        </Wrapper>
      </InputContainer>
      <InputContainer>
        <InputTitle>
          Video Title
        </InputTitle>
        <VideoUrlInput
          value={videoTitle}
          placeholder='Add title (optional)'
          onChange={(e) => {
            setVideoTitle(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleImmediateSave();
            }
          }}
        />
      </InputContainer>
    </Container>
  );
};

VideoTable.propTypes = {
  contentIndex: PropTypes.number.isRequired,
  content: PropTypes.instanceOf(Object).isRequired,
  nonSwappedItemFromParentCalendar: PropTypes.bool.isRequired,
  date: PropTypes.string,
  dayNumber: PropTypes.string,
};

VideoTable.defaultProps = {
  date: null,
  dayNumber: null,
};

export default VideoTable;
